import { Box, Button, Typography } from "@mui/material";
import { BOOKING_STATUS, BOOKING_TYPES } from "constants/general";
import { ROUTES } from "constants/routes";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { updateBookingStatus } from "slices/bookingReducer";
import { updateSelectedBookingData } from "slices/rentalsReducer";
import { useAppDispatch, useAppSelector } from "store/appHooks";
import { convertPrice } from "utils/currency.util";

const SummaryLineItem = ({ label, value, redColorVal }: any) => {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Typography
        sx={{
          color: "#A2A2A2",
          fontSize: "20px",
          fontWeight: 500,
          lineHeight: "30px",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: redColorVal ? "#D21A00" : "#323232",
          fontSize: "20px",
          fontWeight: 500,
          lineHeight: "30px",
        }}
      >
        {new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          currencyDisplay: "narrowSymbol",
        }).format(value)}
      </Typography>
    </Box>
  );
};

const BookingDetailActions = ({ setUploadMode }: { setUploadMode: any }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { rentalsReducer } = useAppSelector((state) => state);
  const { selectedBookingData }: any = rentalsReducer;
  const {
    address,
    user,
    insurance,
    booking_payments,
    booking_status,
    booking_documents,
    booking_type,
    from_date,
    to_date,
  } = selectedBookingData;

  const bookingSummary = booking_payments?.[0];

  const isConfirmedBooking =
    booking_status === BOOKING_STATUS.CONFIRMED &&
    booking_documents?.length === 0;
  const allowApprove =
    booking_status === BOOKING_STATUS.PENDING ||
    booking_status === BOOKING_STATUS.SUGGEST_DATES;
  const isCancelled = booking_status === BOOKING_STATUS.REJECTED;
  const isdocumentsExists = selectedBookingData?.booking_documents?.length > 0;
  const isReturned = booking_status === BOOKING_STATUS.RETURNED;

  const updateStatus = (status: number) =>
    dispatch(
      updateBookingStatus({
        data: {
          id: selectedBookingData.id,
          status: status,
        },
        onSucess: () => {
          dispatch(
            updateSelectedBookingData({
              id: selectedBookingData.id,
              data: {
                booking_status: status,
              },
            })
          );
          navigate(
            `${ROUTES.RENTALS}?type=${
              status === BOOKING_STATUS.CONFIRMED
                ? "confirmed"
                : status === BOOKING_STATUS.REJECTED
                ? "cancelled"
                : "pending"
            }&booking=${selectedBookingData.id}`
          );
        },
      })
    );

  return (
    <Box
      sx={{
        border: "1px solid #E2E2E2",
        borderRadius: "10px",
        padding: { sm: "20px", xs: "20px", md: "30px", lg: "30px", xl: "30px" },
        mt: "20px",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <Box>
          <Typography
            sx={{
              color: "#2A2A2A",
              fontSize: {
                md: "24px",
                lg: "24px",
                xl: "24px",
                sm: "16px",
                xs: "16px",
              },
              fontWeight: 600,
              lineHeight: {
                md: "30px",
                lg: "30px",
                xl: "30px",
                sm: "20px",
                xs: "20px",
              },
            }}
          >
            Delivery location
          </Typography>

          <Box sx={{ mt: "15px" }}>
            <Typography
              sx={{
                color: "#A2A2A2",
                fontWeight: 500,
                fontSize: {
                  md: "16px",
                  lg: "16px",
                  xl: "16px",
                  sm: "13px",
                  xs: "13px",
                },
                lineHeight: "24px",
              }}
            >
              {user?.name}
            </Typography>
            <Typography
              sx={{
                color: "#A2A2A2",
                fontWeight: 500,
                fontSize: {
                  md: "16px",
                  lg: "16px",
                  xl: "16px",
                  sm: "13px",
                  xs: "13px",
                },
                lineHeight: "24px",
              }}
            >
              {`${[
                address.address_line_1,
                address.address_line_2,
                address.city,
                address.state,
                address.city,
                address.country,
                address.zipcode,
              ]
                .filter((r) => r)
                .join(", ")}`}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography
            sx={{
              color: "#2A2A2A",
              fontSize: {
                md: "24px",
                lg: "24px",
                xl: "24px",
                sm: "16px",
                xs: "16px",
              },
              fontWeight: 600,
              lineHeight: {
                md: "30px",
                lg: "30px",
                xl: "30px",
                sm: "20px",
                xs: "20px",
              },
            }}
          >
            Insurance
          </Typography>

          <Box
            sx={{
              border: "1px solid #6E8C20",
              padding: "20px",
              width: "fit-content",
              background: "#E1FFA2",

              borderRadius: "10px",

              display: "flex",
              flexDirection: "column",
              gap: "10px",

              cursor: "pointer",

              boxShadow:
                "0px 3px 6px 0px #25334B1A, 0px 10px 20px 0px #25334B1A",

              mt: "15px",
            }}
            role="button"
          >
            <img
              src={"/assets/img/icons/insurance_checked.svg"}
              alt=""
              width="24"
              height="24"
            />

            <Typography
              sx={{
                color: "#323232",
                fontSize: "15px",
                fontWeight: 500,
                lineHeight: "19px",
              }}
            >
              {insurance.name}
            </Typography>

            <Typography
              sx={{
                color: "#323232",
                fontSize: {
                  md: "24px",
                  lg: "24px",
                  xl: "24px",
                  sm: "16px",
                  xs: "16px",
                },
                fontWeight: 600,
                lineHeight: "31px",
              }}
            >
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                currencyDisplay: "narrowSymbol",
              }).format(insurance.price)}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography
            sx={{
              color: "#2A2A2A",
              fontSize: {
                md: "24px",
                lg: "24px",
                xl: "24px",
                sm: "16px",
                xs: "16px",
              },
              fontWeight: 600,
              lineHeight: {
                md: "30px",
                lg: "30px",
                xl: "30px",
                sm: "20px",
                xs: "20px",
              },
            }}
          >
            Order Summary
          </Typography>
          <Box
            sx={{
              mt: "20px",
              display: "flex",
              flexDirection: "column",
              gap: "15px",
            }}
          >
            <SummaryLineItem
              label="Sub total"
              value={bookingSummary.subtotal}
            />
            <SummaryLineItem
              label="Insurance"
              value={bookingSummary.insurance}
            />

            <SummaryLineItem
              label="Security Deposit"
              value={bookingSummary.security_deposit}
            />
            <SummaryLineItem label="Taxes" value={bookingSummary.taxes} />
            <SummaryLineItem
              label="Haystacker fee"
              value={bookingSummary.haystacker_fees}
              redColorVal={true}
            />
          </Box>
        </Box>

        <Box sx={{ height: "1px", background: "#E2E2E2", width: "100%" }} />

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            sx={{
              color: "#2A2A2A",
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "30px",
            }}
          >
            Total you'll receive
          </Typography>
          <Typography
            sx={{
              color: "#6E8C20",
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "30px",
            }}
          >
            {convertPrice(bookingSummary?.total)}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          mt: "30px",
          display: "flex",
          justifyContent: "flex-end",
          gap: "15px",
        }}
      >
        {(allowApprove || isConfirmedBooking) && (
          <Button
            variant="outlined"
            sx={{ borderRadius: "10px", height: "56px", width: "176px" }}
            size="large"
            onClick={() => updateStatus(BOOKING_STATUS.REJECTED)}
          >
            {isConfirmedBooking ? "Cancel" : "Decline"}
          </Button>
        )}
        {allowApprove && (
          <Button
            variant="contained"
            sx={{ borderRadius: "10px", height: "56px", width: "176px" }}
            size="large"
            onClick={() =>
              updateStatus(
                booking_type === BOOKING_TYPES.MANUAL
                  ? BOOKING_STATUS.PAYMENT_PENDING
                  : BOOKING_STATUS.CONFIRMED
              )
            }
          >
            Approve
          </Button>
        )}
        {isConfirmedBooking && (
          <Button
            variant="contained"
            sx={{ borderRadius: "10px", height: "56px", width: "176px" }}
            size="large"
            onClick={() => setUploadMode("Departure")}
            disabled={
              booking_status === BOOKING_STATUS.PAYMENT_PENDING ||
              !(
                moment().isSameOrAfter(moment(from_date)) &&
                moment().isSameOrBefore(moment(to_date))
              )
            }
          >
            Start
          </Button>
        )}
        {booking_status === BOOKING_STATUS.PAYMENT_PENDING && (
          <Button
            variant="contained"
            sx={{ borderRadius: "10px", height: "35px", width: "176px" }}
            size="large"
            color="warning"
          >
            Payment Pending
          </Button>
        )}
        {isCancelled && (
          <Button
            sx={{
              borderRadius: "10px",
              height: "56px",
              width: {
                md: "176px",
                lg: "176px",
                xl: "176px",
                sm: "100%",
                xs: "100%",
              },
              background: "#FFEFED",
              color: "#D21A00",
            }}
            size="large"
          >
            Cancelled
          </Button>
        )}

        {isdocumentsExists &&
          selectedBookingData?.booking_status === BOOKING_STATUS.STARTED && (
            <Button
              variant="contained"
              sx={{
                borderRadius: "10px",
                height: "56px",
                width: {
                  md: "176px",
                  lg: "176px",
                  xl: "176px",
                  sm: "100%",
                  xs: "100%",
                },
              }}
              size="large"
              onClick={() => setUploadMode("Return")}
            >
              Return
            </Button>
          )}

        {isReturned && (
          <Button
            variant="contained"
            sx={{
              borderRadius: "10px",
              height: "56px",
              width: {
                md: "176px",
                lg: "176px",
                xl: "176px",
                sm: "100%",
                xs: "100%",
              },
            }}
            size="large"
            disabled
          >
            Returned
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default BookingDetailActions;
